/**
 * Safe to Simplify Section
 * MVV opportunities with time savings prominently displayed
 * UX Philosophy: Maximally Assistive, Strategically Transparent
 */

import React from 'react';
import { Zap, CheckCircle, Clock } from 'lucide-react';

const SafeToSimplifySection = ({ tasks, totalSavings, onApplyMVV }) => {
  if (tasks.length === 0) return null;

  const totalHours = (totalSavings / 60).toFixed(1);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <h4 className="text-sm font-bold text-slate-900">Safe to Simplify</h4>
        <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
          {totalHours}h saved
        </span>
      </div>

      <p className="text-xs text-slate-600">
        Use quick versions for these tasks - quality gap is low, time savings are high
      </p>

      <div className="space-y-2">
        {tasks.map(task => (
          <div
            key={task.id}
            className="bg-white rounded-lg p-3 border border-teal-200 hover:border-teal-300 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h5 className="text-sm font-medium text-slate-900 mb-1">
                  {task.title || task.task_name}
                </h5>

                {/* Support reason - explain WHY it's safe */}
                <p className="text-xs text-slate-700 mb-2">
                  {task.supportReason}
                </p>

                {/* Time savings prominently displayed */}
                {task.timeSavings && (
                  <div className="flex items-center gap-1 text-xs font-semibold text-teal-700">
                    <Clock className="w-3 h-3" />
                    <span>Saves {(task.timeSavings / 60).toFixed(1)}h</span>
                  </div>
                )}

                {/* MVV description if available */}
                {task.min_viable_version && (
                  <p className="text-xs text-slate-600 mt-2 italic">
                    Quick version: {task.min_viable_version}
                  </p>
                )}
              </div>

              {/* Check indicator */}
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-teal-50 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-teal-600" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Total Savings Summary */}
      <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-900">Total Time Saved</p>
            <p className="text-xs text-slate-600">Using quick versions for {tasks.length} tasks</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-teal-700">{totalHours}h</p>
          </div>
        </div>
      </div>

      {/* Apply MVV Button */}
      {onApplyMVV && (
        <button
          onClick={() => onApplyMVV(tasks)}
          className="w-full py-2.5 bg-teal-600 text-white rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors shadow-sm"
        >
          Use Quick Versions ({tasks.length} tasks)
        </button>
      )}
    </div>
  );
};

export default SafeToSimplifySection;
